/**
 * Drop Generator — one country: Board | Day timeline | Live | How it works
 * (?tab=, clean URL for board), narrowed to one CAMPAIGN GROUP by the group
 * switcher beside the views (?group=); ?container= / ?master= / ?etab= mount the editor on
 * any tab; ?ch= is the tutorial chapter. Embedded (the SMSWiser country
 * page's Automation tab) it is locked to the open campaign's group — no
 * switcher — and keeps its view under ?dgTab=.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { useCountries } from '@/hooks/smswiser/core/useSMSWiser';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import { CountryHeader } from '@/features/drop-generator/components/board-header/CountryHeader';
import type { EditorTabKey } from '@/features/drop-generator/components/editor/EditorTabs';
import { GroupSwitcher } from '@/features/drop-generator/components/campaign/GroupSwitcher';
import { SimulatedDayNotice } from '@/features/drop-generator/components/live/controls/SimulatedDayNotice';
import { CountryClockProvider } from '@/features/drop-generator/components/shared/time/country-clock-context';
import { useTutorialAutoOpen } from '@/features/drop-generator/components/tutorial/entry/use-tutorial-auto-open';
import { useDayPreview, usePlan, usePlans } from '@/features/drop-generator/useDropGenerator';
import { useDropGeneratorMutations } from '@/features/drop-generator/useDropGeneratorMutations';

import { PlanErrorCard, PlanSkeleton } from '../CountryPageStates';
import { CountryTabs } from '../CountryTabs';
import { useCountryPageParams } from '../useCountryPageParams';
import { CountryEditor } from './CountryEditor';
import { CountryTabContent } from './CountryTabContent';
import { EmbeddedGroupLabel } from './EmbeddedGroupLabel';
import { useLastView } from './last-view/use-last-view';
import { useCampaignScope } from './use-campaign-scope';

interface Props {
  countryKey: string;
  /** Inside another page: locked to this campaign's group. */
  embed?: { campaignWiserId: number };
}

export const DropGeneratorCountryView: FC<Props> = ({ countryKey, embed }) => {
  const { tab, containerParam, masterParam, copyParam, editorTab, chapter, adminPart, at, group, campaign, linkChose, setParams } = useCountryPageParams({ tabKey: embed ? 'dgTab' : 'tab' });
  const countries = useCountries();
  const plans = usePlans();
  const plan = usePlan(countryKey || null);
  const preview = useDayPreview(countryKey || null, tab === 'live' || tab === 'gateways', at);
  const pick = embed ? { group: null, campaign: embed.campaignWiserId } : { group, campaign };
  const scope = useCampaignScope(plan.data, countryKey, pick, preview.data);
  const { createPlan } = useDropGeneratorMutations();
  useTutorialAutoOpen({ containerCount: plan.data?.containers.length, tab, open: () => setParams({ tab: 'tutorial' }) });
  useLastView({
    countryKey,
    ready: !embed && Boolean(plan.data),
    groupKeys: scope.entries.map((e) => e.key),
    current: { tab, group: scope.current?.key ?? null },
    linkChose,
    restore: (view) => setParams({ tab: view.tab === 'board' ? null : view.tab, group: view.group }),
  });

  const countryName = countries.data?.find((c) => c.key === countryKey)?.name ?? countryKey;
  // Editing a master config always opens its campaign group: it belongs to it.
  const openMaster = (id: string | 'new', etab: EditorTabKey | null = null) => {
    const owner = embed ? null : plan.data?.masters?.find((m) => m.id === id)?.campaignGroupKey;
    setParams({ master: id, container: null, etab, copy: null, ...(owner ? { group: owner, campaign: null } : {}) });
  };

  if (plan.isError) {
    return (
      <PlanErrorCard
        countryName={countryName}
        known={plans.data?.some((p) => p.countryKey === countryKey)}
        creating={createPlan.isPending}
        onCreate={() => createPlan.mutate(countryKey)}
      />
    );
  }
  if (!plan.data) return <PlanSkeleton />;

  // The Danish clock (15/10/2026): every time on the page reads Danish first, exact for the day shown.
  const clock = { tz: plan.data.recipientTimezone, flag: countryToFlagEmoji(countryName), runDate: preview.data?.runDate ?? null };
  return (
    <CountryClockProvider value={clock}>
      <div className="flex flex-col gap-5 px-1">
        <CountryHeader
          countryName={countryName}
          plan={plan.data}
          preview={preview.data}
          onOpenGroupAutoSend={(id) => openMaster(id, 'autosend')}
          onOpenContainer={(id) => setParams({ container: id, master: null, etab: null, copy: null })}
          onOpenErrors={() => setParams({ tab: 'errors' })}
          embedded={Boolean(embed)}
        />
        {at && tab !== 'live' ? (
          <SimulatedDayNotice runDate={preview.data?.runDate} onBackToToday={() => setParams({ at: null })} />
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CountryTabs tab={tab} onSelect={(next) => setParams({ tab: next === 'board' ? null : next })} />
          {embed ? (
            <EmbeddedGroupLabel countryKey={countryKey} current={scope.current} />
          ) : tab !== 'tutorial' ? (
            <GroupSwitcher
              entries={scope.entries}
              current={scope.current}
              countryName={countryName}
              totalContainers={plan.data.containers.length}
              canPin={scope.pins.canPin}
              onSelect={(key) => setParams({ group: key, campaign: null })}
              onPin={(entry, on) => scope.pins.toggle(entry.members.map((m) => m.wiserId), on)}
            />
          ) : null}
        </div>
        <CountryTabContent
          tab={tab}
          plan={plan.data}
          preview={preview}
          scope={scope}
          nav={{
            at,
            chapter,
            adminPart,
            setParams,
            onOpenMaster: openMaster,
            onCopyMaster: (id) => setParams({ master: 'new', copy: id, container: null, etab: null }),
            onOpenGroup: embed ? undefined : (key) => setParams({ group: key, campaign: null }),
          }}
        />
        <CountryEditor
          plan={plan.data}
          countryName={countryName}
          containerParam={containerParam}
          masterParam={masterParam}
          copyParam={copyParam}
          editorTab={editorTab}
          group={scope.current}
          setParams={setParams}
          onOpenMaster={openMaster}
        />
      </div>
    </CountryClockProvider>
  );
};
